import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './ViewConfigurations.css'
import deviceConfigSchema from '@/config/deviceConfigurationSchema.json'
import type { DeviceConfigurationSchema, DeviceConfigurationValues } from '@/types/deviceConfiguration'
import { parseSchemaToLayout } from '@/utils/configSchemaParser'
import {unflattenConfiguration} from '@/utils/configUnflattener' 
import { PAGES } from '@/constants/pages'
import NumberField from '@/components/fields/NumberField'
import CheckboxField from '@/components/fields/CheckboxField'
import TextField, { TextType } from '@/components/fields/TextField'
import PermissionGuard from '@/components/PermissionGuard'
import { authService, PermissionScope } from '@/services/AuthService'
import { RESOURCES } from '@/constants/resources'
import { DurationInputService } from '@/services/DurationInputService'
import DurationInput from '@/components/DurationInput'
import { usePartner } from '@/contexts/partnerContext'
import { PartnerConfigurationService } from '@/services/PartnerConfigurationService'
import { PageLayout } from './PageLayout'
import { useLoading } from '../contexts/LoadingContext'
import toast from '../utils/toast'

const schema = deviceConfigSchema as DeviceConfigurationSchema

interface Configuration {
  id: string
  name: string
  description: string
  values?: DeviceConfigurationValues
  deviceCount: number
}

interface ConfigurationDetailsProps {
  configuration: Configuration
  onBack: () => void
  hasInternalUserAccess: boolean
}

function ConfigurationDetails({ configuration, onBack, hasInternalUserAccess }: ConfigurationDetailsProps) {
  const configLayout = useMemo(() => parseSchemaToLayout(schema), [])
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

  // Fields that should be hidden from regular Administrators (only visible to internal SoloProtect users)
  const restrictedFields = [
    'OPTION.SYSTEM.SOFTWARE.UI.HAPTICS_ENABLED',
    'OPTION.SYSTEM.SOFTWARE.MESSAGING.DEFAULT_HEARTBEAT_INTERVAL',
    // All logging options
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_REPORT_INTERVAL',
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_ROTATION_MAX_BYTES',
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_ROTATION_MAX_AGE_MS',
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_ROTATION_MAX_BACKUPS_COUNT',
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_LEVEL',
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_DUMP_AT_STARTUP',
    // Retry intervals across different sections
    'OPTION.APPLICATION.ALERTS.RED_ALERT.RED_ALERT_RETRY_INTERVAL',
    'OPTION.APPLICATION.ALERTS.CHECK_IN_ALERT.CHECK_IN_ALERT_RETRY_INTERVAL',
    'OPTION.APPLICATION.ALERTS.INCAPACITATION_ALERT.INCAPACITATION_ALERT_RETRY_INTERVAL',
    // Incapacitation fields
    'OPTION.APPLICATION.ALERTS.INCAPACITATION_ALERT.INCAPACITATION_ALERT_TIMER_MAXIMUM_VALUE',
    'OPTION.APPLICATION.ALERTS.INCAPACITATION_ALERT.INCAPACITATION_ALERT_TILT_TRIGGER_THRESHOLD_DEGREES'
  ]

  const shouldShowProperty = (key: string) => {
    // Internal users can see everything
    if (hasInternalUserAccess) return true
    // Regular admins cannot see restricted fields
    return !restrictedFields.includes(key)
  }

  const shouldShowSection = (section: { title: string; properties: Array<{ key: string }> }) => {
    // Hide "Logging Options" section entirely for non-internal users
    if (section.title === 'Logging Options' && !hasInternalUserAccess) return false
    // Show section if it has at least one visible property
    return section.properties.some(prop => shouldShowProperty(prop.key))
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const updated = new Set(prev)
      if (updated.has(section)) {
        updated.delete(section)
      } else {
        updated.add(section)
      }
      return updated
    })
  }

  const renderPropertyValue = (key: string) => {
    if (!configuration.values || configuration.values[key] === undefined) {
      return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not set</span>
    }
    return <span>{String(configuration.values[key])}</span>
  }

  return (
    <div className="configuration-details">
      <div className="details-header">
        <button onClick={onBack} className="back-button">↝ Back to Configurations</button>
        <h3>{configuration.name}</h3>
        <p className="configuration-description">{configuration.description}</p>
      </div>

      <div className="configuration-sections">
        {configLayout.sections.filter(shouldShowSection).map(section => (
          <div key={section.title} className="section-item">
            <button
              className="section-header"
              onClick={() => toggleSection(section.title)}
            >
              <span>{section.title}</span>
              <span className="section-toggle">{expandedSections.has(section.title) ? '−' : '+'}</span>
            </button>
            {expandedSections.has(section.title) && (
              <div className="section-content">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {section.properties.filter(property => shouldShowProperty(property.key)).map(property => (
                      <tr key={property.key} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.75rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                          {property.name}
                        </td>
                        <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                          {renderPropertyValue(property.key)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function ViewConfigurationsPage() {
  const { showLoader, hideLoader } = useLoading()

  const configLayout = useMemo(() => parseSchemaToLayout(schema), [])

  const hasInternalUserAccess = authService.hasPermission(RESOURCES.DEFAULT_CONFIG, PermissionScope.CREATE);
  const [configurations, setConfigurations] = useState<Configuration[]>([])
  const [expandedConfigurations, setExpandedConfigurations] = useState<Set<string>>(new Set())
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null)
  const [editingValues, setEditingValues] = useState<DeviceConfigurationValues>({})
  const navigate = useNavigate()
  const { impersonatedPartnerId } = usePartner();

  // Fields that should be hidden from regular PartnerAdministrators (only visible to internal SoloProtect users)
  const restrictedFields = [
    'OPTION.SYSTEM.SOFTWARE.UI.HAPTICS_ENABLED',
    'OPTION.SYSTEM.SOFTWARE.MESSAGING.DEFAULT_HEARTBEAT_INTERVAL',
    // All logging options
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_REPORT_INTERVAL',
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_ROTATION_MAX_BYTES',
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_ROTATION_MAX_AGE_MS',
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_ROTATION_MAX_BACKUPS_COUNT',
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_LEVEL',
    'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_DUMP_AT_STARTUP',
    // Retry intervals across different sections
    'OPTION.APPLICATION.ALERTS.RED_ALERT.RED_ALERT_RETRY_INTERVAL',
    'OPTION.APPLICATION.ALERTS.CHECK_IN_ALERT.CHECK_IN_ALERT_RETRY_INTERVAL',
    'OPTION.APPLICATION.ALERTS.INCAPACITATION_ALERT.INCAPACITATION_ALERT_RETRY_INTERVAL',
    // Incapacitation fields
    'OPTION.APPLICATION.ALERTS.INCAPACITATION_ALERT.INCAPACITATION_ALERT_TIMER_MAXIMUM_VALUE',
    'OPTION.APPLICATION.ALERTS.INCAPACITATION_ALERT.INCAPACITATION_ALERT_TILT_TRIGGER_THRESHOLD_DEGREES'
  ]

  const shouldShowProperty = (key: string) => {
    // Internal users can see everything
    if (hasInternalUserAccess) return true
    // Regular admins cannot see restricted fields
    return !restrictedFields.includes(key)
  }

  const shouldShowSection = (section: { title: string; properties: Array<{ key: string }> }) => {
    // Hide "Logging Options" section entirely for non-internal users
    if (section.title === 'Logging Options' && !hasInternalUserAccess) return false
    // Show section if it has at least one visible property
    return section.properties.some(prop => shouldShowProperty(prop.key))
  }

  useEffect(() => {
    loadConfigurations()
  }, [impersonatedPartnerId])

  const shouldGetConfigurations = impersonatedPartnerId !== null || authService.channelPartnerId !== null;

  const loadConfigurations = async () => {
    try {
      if (!shouldGetConfigurations) {
        setConfigurations([]);
        return;
      };

      showLoader()
      const configurations = (await PartnerConfigurationService.getAll()).configurations

      if (configurations == null || configurations.length == 0) { 
        setConfigurations([]);
        return;
      }

      const mappedConfigurations: Configuration[] = configurations.map(configuration => ({
        id: configuration.id,
        name: configuration.name,
        description: configuration.description || '',
        values: PartnerConfigurationService.jsonToConfigValues(configuration.settingsJson),
        deviceCount: configuration.deviceCount
      }))

      setConfigurations(mappedConfigurations)
    } catch (error) {
      toast.error('Failed to fetch configurations.');
    } finally {
      hideLoader()
    }
  }

  const toggleConfiguration = (configId: string) => {
    setExpandedConfigurations(prev => {
      const updated = new Set(prev)
      if (updated.has(configId)) {
        updated.delete(configId)
      } else {
        updated.add(configId)
      }
      return updated
    })
  }

  const toggleSection = (configId: string, section: string) => {
    const key = `${configId}-${section}`
    setExpandedSections(prev => {
      const updated = new Set(prev)
      if (updated.has(key)) {
        updated.delete(key)
      } else {
        updated.add(key)
      }
      return updated
    })
  }

  const handleEdit = (config: Configuration) => {
    if (!expandedConfigurations.has(config.id)) {
      toggleConfiguration(config.id)
    }
    setEditingConfigId(config.id)
    setEditingValues(config.values || {})
  }

  const handleCancelEdit = (config: Configuration) => {
    toggleConfiguration(config.id)
    setEditingConfigId(null)
    setEditingValues({})
  }

  const handleValueChange = (key: string, value: string | number | boolean, min?: number, max?: number) => {
    
    if (typeof value === 'number') {
      if (max !== undefined && value > max) {
        value = max;
      }
    }

    setEditingValues(prev => ({ ...prev, [key]: value }))
  }

  const handleSaveEdit = async (configId: string) => {
    const config = configurations.find(c => c.id === configId)
    if (!config) return

    try {
      const nestedValues = unflattenConfiguration(editingValues)
      
      const settingsJson = JSON.stringify(nestedValues, null, 2)

      await PartnerConfigurationService.update(configId, config.name, settingsJson, config.description);
      toast.success('Configuration has successfully been updated.');

      setEditingConfigId(null)
      setEditingValues({})
      await loadConfigurations()

      alert('Configuration updated successfully!')
    } catch (error) {
      toast.error('Failed to update configuration.');
    }
  }

  const handleDelete = async (config: Configuration) => {
    if (config.deviceCount > 0) {
      alert(`Cannot delete configuration "${config.name}" because it is assigned to ${config.deviceCount} device(s). Please remove the configuration from all devices before deleting.`)
      return
    }

    if (!confirm(`Are you sure you want to delete the configuration "${config.name}"?`)) {
      return
    }

    try {
      await PartnerConfigurationService.delete(config.id)
      toast.success('Configuration has successfully been deleted.');
      await loadConfigurations()
    } catch (error) {
      toast.error('Failed to delete configuration.');
    }
  }

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  const renderPropertyValue = (key: string, config: Configuration) => {
    const value = getNestedValue(config.values, key); 
    if (value === undefined) {
      return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not set</span>
    }
    return <span>{String(value)}</span>
  }

  const renderEditableField = (property: any, configId: string) => {
    const isEditing = editingConfigId === configId

    // Always get the config from the current state
    const config = configurations.find(c => c.id === configId)
    if (!config) return <span>Error: Config not found</span>

    const value = isEditing ? (editingValues[property.key] ?? getNestedValue(config.values, property.key)) 
      : getNestedValue(config.values, property.key);

    if (!isEditing) {
      return renderPropertyValue(property.key, config)
    }

    // Render editable input based on type
    if (property.type === 'boolean') {
      return (
        <CheckboxField
          value={value !== undefined ? Boolean(value) : Boolean(property.default)}
          onChange={(val) => handleValueChange(property.key, val)}
        />
      )
    } else if (property.type === 'number') {
      return (
        <NumberField
          minimum={property.min}
          maximum={property.max} 
          value={value !== undefined ? Number(value) : Number(property.default)}
          onChange={(val) => handleValueChange(property.key, val, undefined, property.max)}
          onBlur={() => {
            if (property.min !== undefined) {
              const currentVal = value !== undefined ? Number(value) : Number(property.default);
              if (currentVal < property.min) {
                handleValueChange(property.key, property.min);
              }
            }
      }}
        />
      )
    } else if (property.type === 'duration') {
      const { min, max } = DurationInputService.getConstraints(property.key);
      return (
        <DurationInput
            value={String(value)}
            min={min}
            max={max}
            onChange={val => handleValueChange(property.key, val)}
            disabled={property.readonly}
            className="form-input"
        />
      )
    } else if (property.type === 'select' && property.options.length > 0) {
      return (
        <select
          value={value !== undefined ? String(value) : String(property.default)}
          onChange={(e) => handleValueChange(property.key, e.target.value)}
          style={{ width: '100%', padding: '0.5rem' }}
        >
          {property.options.map((option: string) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      )
    } else {
      return (
        <TextField
          value={value !== undefined ? String(value) : String(property.default)}
          onChange={(val) => handleValueChange(property.key, val)}
          type={TextType.TEXT}
        />
      )
    }
  }

  return (
    <PageLayout
      title={PAGES.VIEW_CONFIGURATION.title}
      heading={PAGES.VIEW_CONFIGURATION.heading}
      action={
        shouldGetConfigurations && (
          <PermissionGuard
          resource={[RESOURCES.DEFAULT_CONFIG, RESOURCES.PARTNER_CONFIG]} scope={PermissionScope.CREATE}
          >
            <button className="create-button" onClick={() => navigate('/create-configuration')}>
              Create Configuration
            </button>
          </PermissionGuard>
        )
      }
    >
      {configurations.length === 0 ? (
          <div className="empty-state">
            <p>No configurations found.</p>
          </div>
          ) : (
            <div className="configurations-concertina-list">
                <div className="partner-configurations">
                      {configurations.map(config => (
                        <div className="configuration-concertina-item">
                          <button
                            className="configuration-concertina-header"
                            onClick={() => toggleConfiguration(config.id)}
                          >
                            {/* Configuration Info */}
                            <div className="configuration-info">
                              <span className="configuration-name">{config.name}</span>
                              <span className="configuration-description-inline">{config.description}</span>
                            </div>
                            <div className="configuration-actions">
                              {editingConfigId !== config.id && (
                                <>

                                    <PermissionGuard resource={[RESOURCES.DEFAULT_CONFIG, RESOURCES.PARTNER_CONFIG]} scope={PermissionScope.UPDATE}>
                                      <button
                                        className="btn-edit"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleEdit(config)
                                        }}
                                      >
                                        Edit
                                      </button>
                                    </PermissionGuard>
                                </>
                              )}

                            <PermissionGuard resource={[RESOURCES.DEFAULT_CONFIG, RESOURCES.PARTNER_CONFIG]} scope={PermissionScope.DELETE}>
                                <button
                                  className="btn-delete"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDelete(config)
                                  }}>
                                    Delete
                                </button>
                              </PermissionGuard>
                              <span className="configuration-toggle">
                                {expandedConfigurations.has(config.id) ? '−' : '+'}
                              </span>
                            </div>
                          </button>
                          {expandedConfigurations.has(config.id) && (
                            <div className="configuration-concertina-content">
                              <div className="configuration-sections">
                                {configLayout.sections.filter(shouldShowSection).map(section => {
                                  const sectionKey = `${config.id}-${section.title}`
                                  return (
                                    <div key={section.title} className="nested-section-item">
                                      <button
                                        className="nested-section-header"
                                        onClick={() => toggleSection(config.id, section.title)}
                                      >
                                        <span>{section.title}</span>
                                        <span className="nested-section-toggle">
                                          {expandedSections.has(sectionKey) ? '−' : '+'}
                                        </span>
                                      </button>
                                      {expandedSections.has(sectionKey) && (
                                        <div className="nested-section-content">
                                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <tbody>
                                              {section.properties.filter(property => shouldShowProperty(property.key)).map(property => (
                                                <tr key={property.key} style={{ borderBottom: '1px solid var(--border)' }}>
                                                  <td style={{ padding: '0.75rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                                                    {property.name}
                                                  </td>
                                                  <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                                                    {renderEditableField(property, config.id)}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                              {editingConfigId === config.id && (
                                <div className="edit-actions">
                                  <button className="btn-cancel" onClick={() => handleCancelEdit(config)}>
                                    Cancel
                                  </button>
                                  <button className="btn-save" onClick={() => handleSaveEdit(config.id)}>
                                    Save
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                </div>
            </div>
          )}
    </PageLayout>
  )
}
